import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { fmtTRY } from "../lib/format";
import { PageHeader } from "../components/Common";
import {
  TrendUp, TrendDown, Wallet, Car, FileText, Bank, ArrowUpRight, ArrowDownRight, Warning,
  FileXls, FilePdf, Bell, EnvelopeSimple, Percent, Receipt,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import jsPDF from "jspdf";

const COLORS = ["#002FA7", "#0891b2", "#65a30d", "#ca8a04", "#dc2626", "#7c3aed"];

function KpiCard({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div
      className="bg-white border border-zinc-200 rounded-sm p-6 hover:-translate-y-px hover:shadow-sm transition-all duration-200"
      data-testid={testid}
    >
      <div className="flex items-start justify-between">
        <span className="text-xs uppercase tracking-[0.15em] font-semibold text-zinc-500">{label}</span>
        <Icon size={18} weight="bold" className={accent || "text-zinc-400"} />
      </div>
      <div className="font-mono text-2xl font-semibold text-zinc-950 mt-3 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

const nf = (v) => new Intl.NumberFormat("tr-TR", { notation: "compact", maximumFractionDigits: 1 }).format(v);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [period, setPeriod] = useState("all");
  const [reminders, setReminders] = useState([]);
  const [prof, setProf] = useState([]);
  const [vatMonths, setVatMonths] = useState(12);
  const reportRef = useRef(null);

  useEffect(() => {
    api.get(`/dashboard?period=${period}`).then(({ data }) => setD(data));
  }, [period]);

  useEffect(() => {
    api.get("/reminders").then(({ data }) => setReminders(data));
    api.get("/vehicle-profitability").then(({ data }) => setProf(data));
  }, []);

  const downloadReport = async (fmt) => {
    try {
      const { data } = await api.get(`/reports/${fmt}?period=${period}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `filofinans-rapor.${fmt === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rapor indirildi");
    } catch {
      toast.error("Rapor indirilemedi");
    }
  };

  const sendEmail = async () => {
    toast.loading("E-posta gönderiliyor...", { id: "mail" });
    try {
      const { data } = await api.post(`/reports/email?period=${period}`);
      toast.success(`Finansal özet ${data.recipient} adresine gönderildi`, { id: "mail" });
    } catch {
      toast.error("E-posta gönderilemedi", { id: "mail" });
    }
  };

  const sendReminderEmail = async () => {
    toast.loading("Gönderiliyor...", { id: "rem" });
    try {
      const { data } = await api.post("/reminders/email");
      if (data.status === "empty") toast.info(data.message, { id: "rem" });
      else toast.success(`Hatırlatma ${data.recipient} adresine gönderildi`, { id: "rem" });
    } catch {
      toast.error("Gönderilemedi", { id: "rem" });
    }
  };

  const downloadDashboardPdf = async () => {
    if (!reportRef.current) return;
    toast.loading("Panel PDF hazırlanıyor...", { id: "dpdf" });
    try {
      const dataUrl = await toPng(reportRef.current, { backgroundColor: "#FAFAFA", pixelRatio: 2, cacheBust: true });
      const img = new Image();
      img.src = dataUrl;
      await new Promise((res) => { img.onload = res; });
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgH = (img.height * pageW) / img.width;
      let heightLeft = imgH;
      let position = 0;
      pdf.addImage(dataUrl, "PNG", 0, position, pageW, imgH);
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(dataUrl, "PNG", 0, position, pageW, imgH);
        heightLeft -= pageH;
      }
      pdf.save("filofinans-panel.pdf");
      toast.success("Panel PDF indirildi", { id: "dpdf" });
    } catch {
      toast.error("PDF oluşturulamadı", { id: "dpdf" });
    }
  };

  if (!d) return <div className="p-8 text-zinc-500">Yükleniyor...</div>;

  const PERIODS = [["month", "Bu Ay"], ["quarter", "Bu Çeyrek"], ["year", "Bu Yıl"], ["all", "Tümü"]];

  return (
    <div className="p-8 max-w-[1600px]" ref={reportRef}>
      <PageHeader title="Gösterge Paneli" subtitle="Filo finansal performansınızın genel görünümü" action={
        <div className="flex items-center gap-3">
          <div className="flex border border-zinc-200 rounded-sm overflow-hidden bg-white" data-testid="period-selector">
            {PERIODS.map(([v, l]) => (
              <button key={v} onClick={() => setPeriod(v)} data-testid={`period-${v}`}
                className={`px-3 py-2 text-xs font-semibold transition-colors ${period === v ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>{l}</button>
            ))}
          </div>
          <button onClick={() => downloadReport("excel")} data-testid="export-excel" className="flex items-center gap-1.5 border border-zinc-200 bg-white rounded-sm px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-100 transition-colors"><FileXls size={16} weight="bold" className="text-green-600" />Excel</button>
          <button onClick={() => downloadReport("pdf")} data-testid="export-pdf" className="flex items-center gap-1.5 border border-zinc-200 bg-white rounded-sm px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-100 transition-colors"><FilePdf size={16} weight="bold" className="text-red-600" />PDF</button>
          <button onClick={downloadDashboardPdf} data-testid="dashboard-pdf" className="flex items-center gap-1.5 border border-zinc-200 bg-white rounded-sm px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-100 transition-colors"><FilePdf size={16} weight="bold" className="text-[#002FA7]" />Panel PDF</button>
          <button onClick={sendEmail} data-testid="send-email" className="flex items-center gap-1.5 border border-zinc-200 bg-white rounded-sm px-3 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-100 transition-colors"><EnvelopeSimple size={16} weight="bold" className="text-[#002FA7]" />E-posta</button>
        </div>
      } />

      {/* North star + KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4 animate-fade-up">
        <div className="lg:col-span-2 bg-[#09090B] text-white rounded-sm p-6 grain relative overflow-hidden" data-testid="kpi-net-profit">
          <span className="text-xs uppercase tracking-[0.15em] font-semibold text-zinc-400 relative z-10">Net Kâr</span>
          <div className="font-mono text-4xl font-bold mt-2 relative z-10 tabular-nums">{fmtTRY(d.net_profit)}</div>
          <div className="flex items-center gap-4 mt-4 relative z-10">
            <div className="flex items-center gap-1.5 text-sm">
              <ArrowUpRight size={16} weight="bold" className="text-green-400" />
              <span className="text-zinc-300">Gelir</span>
              <span className="font-mono text-green-400">{fmtTRY(d.total_income)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm">
              <ArrowDownRight size={16} weight="bold" className="text-red-400" />
              <span className="text-zinc-300">Gider</span>
              <span className="font-mono text-red-400">{fmtTRY(d.total_expense)}</span>
            </div>
          </div>
        </div>
        <KpiCard label="Nakit Pozisyon" value={fmtTRY(d.cash_position)} sub="Net kâr − kalan borç" icon={Wallet} accent="text-[#002FA7]" testid="kpi-cash" />
        <KpiCard label="Filo Doluluk" value={`%${d.utilization}`} sub={`${d.rented}/${d.total_vehicles} araç kirada`} icon={Car} accent="text-[#002FA7]" testid="kpi-utilization" />
        <KpiCard label="Kâr Marjı" value={`%${d.profit_margin}`} sub="Net kâr / gelir" icon={Percent} accent={d.profit_margin >= 0 ? "text-green-600" : "text-red-600"} testid="kpi-margin" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8 animate-fade-up">
        <KpiCard label="Toplam Gelir" value={fmtTRY(d.total_income)} icon={TrendUp} accent="text-green-600" testid="kpi-income" />
        <KpiCard label="Toplam Gider" value={fmtTRY(d.total_expense)} icon={TrendDown} accent="text-red-600" testid="kpi-expense" />
        <KpiCard label="Aktif Sözleşme" value={d.active_contracts} icon={FileText} testid="kpi-contracts" />
        <KpiCard label="Gecikmiş" value={d.overdue_contracts} icon={Warning} accent="text-red-600" testid="kpi-overdue" />
        <KpiCard label="Kalan Borç" value={fmtTRY(d.total_debt)} icon={Bank} accent="text-[#002FA7]" testid="kpi-debt" />
        <KpiCard label="Aylık Taksit" value={fmtTRY(d.monthly_installments)} icon={Bank} testid="kpi-installment" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 animate-fade-up" data-testid="vat-summary">
        <KpiCard label="Hesaplanan KDV" value={fmtTRY(d.collected_vat)} sub="Gelirlerden" icon={Receipt} accent="text-green-600" testid="kpi-collected-vat" />
        <KpiCard label="İndirilecek KDV" value={fmtTRY(d.paid_vat)} sub="Giderlerden" icon={Receipt} accent="text-red-600" testid="kpi-paid-vat" />
        <KpiCard label="Ödenecek Net KDV" value={fmtTRY(d.net_vat)} sub="Hesaplanan − indirilecek" icon={Receipt} accent="text-[#002FA7]" testid="kpi-net-vat" />
      </div>

      {reminders.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-8 animate-fade-up" data-testid="reminders-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell size={18} weight="bold" className="text-[#002FA7]" />
              <h3 className="font-display text-lg font-semibold text-zinc-900">Ödeme Hatırlatıcıları</h3>
              <span className="text-xs font-mono text-zinc-400">({reminders.length})</span>
            </div>
            <button onClick={sendReminderEmail} data-testid="send-reminder-email" className="flex items-center gap-1.5 border border-zinc-200 rounded-sm px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-100 transition-colors"><EnvelopeSimple size={14} weight="bold" className="text-[#002FA7]" />Hatırlatma Gönder</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {reminders.map((r, i) => {
              const cls = r.severity === "danger" ? "border-l-red-500 bg-red-50/40" : r.severity === "warning" ? "border-l-yellow-500 bg-yellow-50/40" : "border-l-zinc-300";
              return (
                <div key={`${r.category}-${r.title}-${i}`} className={`border border-zinc-200 border-l-4 rounded-sm px-4 py-3 ${cls}`} data-testid={`reminder-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-zinc-400">{r.category}</span>
                    {r.days != null && <span className="text-[11px] font-mono font-semibold text-zinc-600">{r.days} gün</span>}
                  </div>
                  <div className="text-sm font-medium text-zinc-900 mt-1">{r.title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{r.detail}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {prof.length > 0 && (() => {
        const topProfit = prof.filter((r) => r.profit > 0).slice(0, 3);
        const topLoss = prof.filter((r) => r.profit < 0).sort((a, b) => a.profit - b.profit).slice(0, 3);
        if (topProfit.length === 0 && topLoss.length === 0) return null;
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 animate-fade-up" data-testid="roi-summary">
            <RoiList title="En Kârlı Araçlar" icon={TrendUp} items={topProfit} positive emptyText="Kârlı araç yok" />
            <RoiList title="En Zararlı Araçlar" icon={TrendDown} items={topLoss} emptyText="Zararlı araç yok" />
          </div>
        );
      })()}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm p-6" data-testid="chart-trend">
          <h3 className="font-display text-lg font-semibold text-zinc-900 mb-1">Gelir & Gider Trendi</h3>
          <p className="text-xs text-zinc-500 mb-4">Son {d.trend.length} ay</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={d.trend} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f4" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#71717a" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={nf} tick={{ fontSize: 11, fill: "#a1a1aa", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => fmtTRY(v)} contentStyle={{ borderRadius: 4, border: "1px solid #e4e4e7", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="gelir" name="Gelir" fill="#002FA7" radius={[2, 2, 0, 0]} />
              <Bar dataKey="gider" name="Gider" fill="#dc2626" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="chart-expense-breakdown">
          <h3 className="font-display text-lg font-semibold text-zinc-900 mb-1">Gider Dağılımı</h3>
          <p className="text-xs text-zinc-500 mb-4">Kategori bazında</p>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={d.expense_breakdown} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={80} innerRadius={45}>
                {d.expense_breakdown.map((entry, i) => (
                  <Cell key={entry.name || i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmtTRY(v)} contentStyle={{ borderRadius: 4, border: "1px solid #e4e4e7", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid="chart-net">
        <h3 className="font-display text-lg font-semibold text-zinc-900 mb-1">Aylık Net Nakit Akışı</h3>
        <p className="text-xs text-zinc-500 mb-4">Gelir − gider</p>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={d.trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f4" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#71717a" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={nf} tick={{ fontSize: 11, fill: "#a1a1aa", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v) => fmtTRY(v)} contentStyle={{ borderRadius: 4, border: "1px solid #e4e4e7", fontSize: 12 }} />
            <Line type="monotone" dataKey="net" name="Net" stroke="#002FA7" strokeWidth={2.5} dot={{ r: 3, fill: "#002FA7" }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-6 mt-4 animate-fade-up" data-testid="vat-declaration">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold text-zinc-900 mb-1">KDV Beyan Özeti</h3>
            <p className="text-xs text-zinc-500">Aylık hesaplanan, indirilecek ve ödenecek net KDV</p>
          </div>
          <div className="flex border border-zinc-200 rounded-sm overflow-hidden bg-white" data-testid="vat-period-selector">
            {[[1, "Ay"], [3, "Çeyrek"], [12, "Yıl"]].map(([n, l]) => (
              <button key={n} onClick={() => setVatMonths(n)} data-testid={`vat-period-${n}`} className={`px-3 py-1.5 text-xs font-semibold transition-colors ${vatMonths === n ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>{l}</button>
            ))}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left font-semibold text-zinc-600 px-4 py-2 text-xs uppercase tracking-wider">Ay</th>
              <th className="text-right font-semibold text-zinc-600 px-4 py-2 text-xs uppercase tracking-wider">Hesaplanan</th>
              <th className="text-right font-semibold text-zinc-600 px-4 py-2 text-xs uppercase tracking-wider">İndirilecek</th>
              <th className="text-right font-semibold text-zinc-600 px-4 py-2 text-xs uppercase tracking-wider">Ödenecek KDV</th>
            </tr>
          </thead>
          <tbody>
            {d.vat_monthly.slice(-vatMonths).map((m, i) => (
              <tr key={m.month || i} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`vat-row-${i}`}>
                <td className="px-4 py-2 font-medium text-zinc-900">{m.month}</td>
                <td className="px-4 py-2 text-right font-mono text-green-600 tabular-nums">{fmtTRY(m.collected)}</td>
                <td className="px-4 py-2 text-right font-mono text-red-600 tabular-nums">{fmtTRY(m.paid)}</td>
                <td className={`px-4 py-2 text-right font-mono font-semibold tabular-nums ${m.payable >= 0 ? "text-[#002FA7]" : "text-zinc-500"}`}>{fmtTRY(m.payable)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function RoiList({ title, icon: Icon, items, positive, emptyText }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-6" data-testid={`roi-${positive ? "top" : "bottom"}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} weight="bold" className={positive ? "text-green-600" : "text-red-600"} />
        <h3 className="font-display text-lg font-semibold text-zinc-900">{title}</h3>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-zinc-400 py-4 text-center">{emptyText}</div>
      ) : (
        <div className="space-y-2">
          {items.map((r, i) => {
            const roi = r.purchase > 0 ? (r.profit / r.purchase) * 100 : null;
            return (
              <div key={r.plate} className="flex items-center justify-between border-b border-zinc-100 last:border-0 pb-2 last:pb-0" data-testid={`roi-item-${r.plate}`}>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs text-zinc-400 w-4">{i + 1}</span>
                  <div>
                    <div className="font-mono font-semibold text-sm text-zinc-900">{r.plate}</div>
                    <div className="text-xs text-zinc-500">{r.vehicle}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`font-mono text-sm font-semibold tabular-nums ${r.profit >= 0 ? "text-green-600" : "text-red-600"}`}>{fmtTRY(r.profit)}</div>
                  {roi !== null && <div className={`text-[11px] font-mono ${roi >= 0 ? "text-[#002FA7]" : "text-red-500"}`}>ROI %{roi.toFixed(1)}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
